import React from 'react';
import { NavLink } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import {
  LayoutDashboard, ClipboardList, MessageSquare, Truck, Users, DollarSign,
  CreditCard, Palette, ShieldCheck, Wrench, BarChart3,
} from 'lucide-react';

/**
 * Mobile-only bottom navigation. Shows 4 primary destinations per role with
 * large, thumb-reachable touch targets. Hidden on lg screens.
 */
const BOTTOM_NAV_BY_ROLE = {
  super_admin: [
    { to: '/dashboard',           label: 'Home',     icon: LayoutDashboard },
    { to: '/admin/subscriptions', label: 'Subs',     icon: CreditCard },
    { to: '/admin/leads',         label: 'Leads',    icon: MessageSquare },
    { to: '/profile',             label: 'Me',       icon: ShieldCheck },
  ],
  company_admin: [
    { to: '/dashboard',           label: 'Home',     icon: LayoutDashboard },
    { to: '/loads',               label: 'Loads',    icon: ClipboardList },
    { to: '/drivers',             label: 'Drivers',  icon: Users },
    { to: '/messages',            label: 'Chat',     icon: MessageSquare },
    { to: '/payroll',             label: 'Payroll',  icon: DollarSign },
  ],
  dispatcher: [
    { to: '/dashboard',           label: 'Home',     icon: LayoutDashboard },
    { to: '/loads',               label: 'Loads',    icon: ClipboardList },
    { to: '/drivers',             label: 'Drivers',  icon: Users },
    { to: '/messages',            label: 'Chat',     icon: MessageSquare },
    { to: '/assets',              label: 'Assets',   icon: Wrench },
  ],
  driver: [
    { to: '/dashboard',           label: 'Loads',    icon: Truck },
    { to: '/my-assets',           label: 'Asset',    icon: Wrench },
    { to: '/my-pay',              label: 'Pay',      icon: DollarSign },
    { to: '/messages',            label: 'Chat',     icon: MessageSquare },
    { to: '/profile',             label: 'Me',       icon: BarChart3 },
  ],
};

const BottomNav = () => {
  const { user } = useAuth();
  if (!user) return null;
  const items = BOTTOM_NAV_BY_ROLE[user.role] || [];
  if (items.length === 0) return null;

  return (
    <nav
      data-testid="mobile-bottom-nav"
      className="lg:hidden fixed bottom-0 inset-x-0 z-40 bg-white border-t border-slate-200 flex items-stretch justify-around shadow-[0_-4px_12px_rgba(0,0,0,0.04)]"
      style={{ paddingBottom: 'max(env(safe-area-inset-bottom), 4px)' }}
    >
      {items.map((item) => {
        const Icon = item.icon;
        return (
          <NavLink
            key={item.to}
            to={item.to}
            end={item.to === '/dashboard'}
            data-testid={`bottom-nav-${item.label.toLowerCase()}`}
            className={({ isActive }) =>
              `flex-1 flex flex-col items-center justify-center gap-0.5 py-2 min-h-[56px] text-[11px] transition-colors ${
                isActive
                  ? 'text-[var(--brand-primary,#002FA7)] font-semibold'
                  : 'text-slate-500 hover:text-slate-900'
              }`
            }
            style={{ fontFamily: 'IBM Plex Sans, sans-serif' }}
          >
            <Icon className="w-5 h-5" strokeWidth={2} />
            <span className="leading-none">{item.label}</span>
          </NavLink>
        );
      })}
    </nav>
  );
};

export default BottomNav;
